"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { BrandConfig } from "./types";
import { DEFAULT_BRAND, resolveBrand } from "./brands";

const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND);

export function useBrand(): BrandConfig {
  return useContext(BrandContext);
}

/**
 * White-label provider.
 * Resolves the brand from the hostname (subdomain per brand),
 * then paints runtime CSS variables so the whole UI re-skins
 * without rebuilds. Auto-generated logo derives from brand colors.
 */
export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<BrandConfig>(DEFAULT_BRAND);

  useEffect(() => {
    setBrand(resolveBrand(window.location.hostname));
  }, []);

  const style = useMemo<React.CSSProperties>(
    () => ({
      ["--brand-primary" as string]: brand.colors.primary,
      ["--brand-accent" as string]: brand.colors.accent,
      ["--brand-gradient" as string]: `linear-gradient(135deg, ${brand.colors.primary}, ${brand.colors.accent})`,
    }),
    [brand]
  );

  return (
    <BrandContext.Provider value={brand}>
      <div style={style} className="min-h-screen">
        {children}
      </div>
    </BrandContext.Provider>
  );
}
